import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Product } from '../types';
import { supabase } from '../lib/supabaseClient';
import PriceWithVatLabel from './PriceWithVatLabel';
import ProductNewRibbon from './ProductNewRibbon';
import { trackProductLinkClick } from '../utils/siteAnalytics';

interface RelatedProductsProps {
  currentProductId: string;
  category: string;
  limit?: number;
}

const RelatedProducts: React.FC<RelatedProductsProps> = ({ 
  currentProductId, 
  category,
  limit = 3
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRelatedProducts();
  }, [currentProductId, category]);

  const fetchRelatedProducts = async () => {
    try {
      // Fetch products in the same category, excluding the current product
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category', category)
        .neq('id', currentProductId)
        .eq('hidden_from_products', false)
        .limit(limit);

      if (error) throw error;

      const transformedProducts = data.map(product => ({
        id: product.id,
        name: product.name,
        category: product.category,
        pricePerDay: product.price_per_day,
        priceOnRequest: Boolean(product.price_on_request),
        description: product.description,
        shortDescription: product.short_description,
        image: product.image,
        gallery: product.gallery || [],
        popular: product.popular || false,
        featured: product.featured || false,
        includes: product.includes || [],
        upgrades: product.upgrades || [],
        slug: product.slug,
        newUntil: product.new_until ?? null,
      }));

      setProducts(transformedProducts);
    } catch (err) {
      console.error('Error fetching related products:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-100 rounded-none h-64"></div>
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-white">Liknande produkter</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {products.map(product => (
          <div key={product.id} className="bg-white rounded-none shadow-md overflow-visible hover:shadow-lg transition-shadow duration-300">
            <div className="relative h-48 bg-gray-100 overflow-hidden">
              <ProductNewRibbon newUntil={product.newUntil} size="md" />
              <img 
                src={product.image} 
                alt={product.name} 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg mb-2">{product.name}</h3>
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.shortDescription}</p>
              <div className="flex justify-between items-center gap-3">
                {product.priceOnRequest ? (
                  <div>
                    <div className="font-bold text-purple-600">Enligt offert</div>
                    <div className="text-[11px] text-slate-500">Begär offert</div>
                  </div>
                ) : (
                  <PriceWithVatLabel
                    price={product.pricePerDay}
                    valueClassName="font-bold text-purple-600"
                    labelClassName="text-[11px] text-slate-500"
                    suffix="/dag"
                  />
                )}
                <Link 
                  to={`/produkter/${product.slug || product.id}`}
                  onClick={() => trackProductLinkClick(product, 'Relaterade produkter')}
                  className="text-purple-600 hover:text-purple-700 flex items-center"
                >
                  Mer info <ArrowRight size={16} className="ml-1" />
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RelatedProducts;